import React, { useRef, useMemo, useState } from 'react';
import { ViewerPropertiesComplete, getExtension } from '../model';

import { use3dHook } from './common/3d/use3dHook';

// SVG icons for the control buttons
const FitIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GridIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="3" x2="21" y2="3"></line>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="3" y1="15" x2="21" y2="15"></line>
    <line x1="3" y1="21" x2="21" y2="21"></line>
    <line x1="3" y1="3" x2="3" y2="21"></line>
    <line x1="9" y1="3" x2="9" y2="21"></line>
    <line x1="15" y1="3" x2="15" y2="21"></line>
    <line x1="21" y1="3" x2="21" y2="21"></line>
  </svg>
);

const TransparentIcon: React.FC<{ isTransparent: boolean }> = ({ isTransparent }) => {
  if (isTransparent) {
    // Moon icon for transparent background
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
      </svg>
    );
  }
  // Sun icon for dark background
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="5"></circle>
      <line x1="12" y1="1" x2="12" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="23"></line>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
      <line x1="1" y1="12" x2="3" y2="12"></line>
      <line x1="21" y1="12" x2="23" y2="12"></line>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
    </svg>
  );
};

const ResetIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
    <path d="M21 3v5h-5"></path>
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
    <path d="M3 21v-5h5"></path>
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const SpinnerIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ animation: 'spin 1s linear infinite' }}
  >
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
  </svg>
);

// Parse progress string to extract percentage
const parseProgress = (progressStr: string | null): number | null => {
  if (!progressStr) return null;
  const match = progressStr.match(/(\d+(?:\.\d+)?)\s*%/);
  return match ? parseFloat(match[1]) : null;
};

export const Media3D: React.FC<ViewerPropertiesComplete> = (props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [expandedMissingTextures, setExpandedMissingTextures] = useState(false);
  const [expandedLoaderWarnings, setExpandedLoaderWarnings] = useState(false);
  const { error, loadResult, progress, controls, animations, selectedAnimation } = use3dHook(
    hostRef,
    getExtension(props.filename),
    props.src,
    props.filename,
    !props.attachEventsToDocument,
  );

  const progressPercent = useMemo(() => parseProgress(progress), [progress]);
  const isLoading = progress !== null;
  const hasAnimations = animations.length > 0;
  const failedTextureCount = loadResult?.failedTextures?.length ?? 0;
  const hasFailedTextures = failedTextureCount > 0;
  const loaderWarningCount = loadResult?.loaderWarnings?.length ?? 0;
  const hasLoaderWarnings = loaderWarningCount > 0;

  return (
    <div className="gen-media-3d gen-file-viewer__container">
      <div ref={hostRef} className="gen-media-3d__host" />

      {/* Control buttons toolbar */}
      <div
        className={`gen-media-3d__toolbar${props.compact ? ' gen-media-3d__toolbar--compact' : ''}`}
      >
        <button
          className="gen-media-3d__toolbar-btn"
          onClick={controls.zoomToFit}
          title="Fit to view"
        >
          <FitIcon />
        </button>
        <button
          className="gen-media-3d__toolbar-btn"
          onClick={() => controls.setUpAxisTo('Y')}
          title="Y-up"
          data-active={controls.upAxis === 'Y'}
        >
          Y
        </button>
        <button
          className="gen-media-3d__toolbar-btn"
          onClick={() => controls.setUpAxisTo('Z')}
          title="Z-up"
          data-active={controls.upAxis === 'Z'}
        >
          Z
        </button>
        {hasAnimations && (
          <>
            <div className="gen-media-3d__toolbar-separator" />
            <select
              className="gen-media-3d__toolbar-select"
              value={selectedAnimation ?? ''}
              onChange={(e) => controls.selectAnimation(e.target.value || null)}
              title="Select animation"
            >
              <option value="">Static (no animation)</option>
              {animations.map((anim, idx) => (
                <option key={anim.uuid || idx} value={anim.name}>
                  {anim.name || `Animation ${idx + 1}`}
                </option>
              ))}
            </select>
          </>
        )}

        <div className="gen-media-3d__toolbar-separator" />

        <button
          className="gen-media-3d__toolbar-btn"
          onClick={controls.toggleGrid}
          title="Toggle grid"
          data-active={controls.gridVisible}
        >
          <GridIcon />
        </button>

        <button
          className="gen-media-3d__toolbar-btn"
          onClick={controls.toggleTransparent}
          title="Toggle transparent background"
          data-active={controls.transparentBackground}
        >
          <TransparentIcon isTransparent={controls.transparentBackground} />
        </button>

        <button
          className="gen-media-3d__toolbar-btn"
          onClick={controls.resetRenderSettings}
          title="Reset render settings"
        >
          <ResetIcon />
        </button>

        <div className="gen-media-3d__toolbar-separator" />

        <button
          className="gen-media-3d__toolbar-btn"
          onClick={controls.handleDownload}
          disabled={controls.isDownloading || !loadResult}
          title="Download model (ZIP with GLB export)"
        >
          {controls.isDownloading ? <SpinnerIcon /> : <DownloadIcon />}
        </button>
      </div>

      <div className="gen-media-3d__overlay">
        {/* Loading Progress */}
        {isLoading && (
          <div className="gen-media-3d__loading">
            <div className="gen-media-3d__loading-content">
              <div className="gen-media-3d__loading-spinner"></div>
              <div className="gen-media-3d__loading-text">Loading model...</div>
              <div className="gen-media-3d__progress-bar">
                <div
                  className="gen-media-3d__progress-fill"
                  style={{ width: `${progressPercent || 0}%` }}
                >
                  <div className="gen-media-3d__progress-shimmer"></div>
                </div>
              </div>
              {progress && <div className="gen-media-3d__progress-text">{progress}</div>}
            </div>
          </div>
        )}

        {/* Status Messages */}
        <div className="gen-media-3d__stats">
          {error !== null && (
            <div className="gen-media-3d__message gen-media-3d__message--error">
              <span className="gen-media-3d__message-icon">⚠️</span>
              <span className="gen-media-3d__message-text">Error: {error}</span>
            </div>
          )}
          {loadResult?.hasMeshes === false && (
            <div className="gen-media-3d__message gen-media-3d__message--warning">
              <span className="gen-media-3d__message-icon">⚠️</span>
              <span className="gen-media-3d__message-text">
                This file does not contain mesh data
              </span>
            </div>
          )}
          {hasFailedTextures && (
            <div className="gen-media-3d__message gen-media-3d__message--info">
              <button
                className="gen-media-3d__message-toggle"
                onClick={() => setExpandedMissingTextures(!expandedMissingTextures)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <span className="gen-media-3d__message-icon">
                  {expandedMissingTextures ? '▼' : '▶'} ⚠️
                </span>
                <span className="gen-media-3d__message-text">
                  {failedTextureCount} texture(s) were not loaded
                </span>
              </button>
              {expandedMissingTextures && (
                <div
                  style={{
                    marginTop: '12px',
                    paddingLeft: '24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '8px',
                    fontSize: '0.9em',
                  }}
                >
                  {loadResult!.failedTextures.map((tex, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '6px 8px',
                        backgroundColor: 'rgba(255, 165, 0, 0.1)',
                        border: '1px solid rgba(255, 165, 0, 0.3)',
                        borderRadius: '3px',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        color: '#fff',
                      }}
                    >
                      {tex}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {hasLoaderWarnings && (
            <div className="gen-media-3d__message gen-media-3d__message--warning">
              <button
                className="gen-media-3d__message-toggle"
                onClick={() => setExpandedLoaderWarnings(!expandedLoaderWarnings)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <span className="gen-media-3d__message-icon">
                  {expandedLoaderWarnings ? '▼' : '▶'} ⚠️
                </span>
                <span className="gen-media-3d__message-text">
                  {loaderWarningCount} loader warning(s)
                </span>
              </button>
              {expandedLoaderWarnings && (
                <div
                  style={{
                    marginTop: '12px',
                    paddingLeft: '24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '8px',
                    fontSize: '0.9em',
                  }}
                >
                  {loadResult!.loaderWarnings.map((warning, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 10px',
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid rgba(255, 193, 7, 0.4)',
                        borderRadius: '3px',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        whiteSpace: 'pre-wrap',
                        color: '#fff',
                      }}
                    >
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
